// src/components/ServicesSection.jsx
import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  IconButton,
  useTheme,
  useMediaQuery,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  FaLaptopCode,
  FaBolt,
  FaShoppingCart,
  FaBullhorn,
  FaCloud,
  FaRobot,
  FaVrCardboard,
  FaGamepad,
} from 'react-icons/fa';
import KeyboardArrowLeft  from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import CheckCircleIcon    from '@mui/icons-material/CheckCircle';

const services = [
  {
    Icon: FaLaptopCode,
    title: 'Web & Mobile Development',
    points: [
      'PWAs: fast, offline-capable & installable.',
      'Headless e-commerce: Shopify/BigCommerce or custom CMS + Next.js/Gatsby.',
      'API & Microservices: scalable REST/GraphQL back-ends.',
      'Mobile-First: pixel-perfect across devices.',
    ],
  },
  {
    Icon: FaBolt,
    title: 'AI & Automation',
    points: [
      'GPT-powered chatbots & voice assistants.',
      'Generative content pipelines: auto blog posts & copy.',
      'Image/Video AI: upscaling, tracking & style transfer.',
      'Workflow automation: Zapier-style flows & lead bots.',
    ],
  },
  {
    Icon: FaShoppingCart,
    title: 'E-Commerce & Dropshipping',
    points: [
      'Full-stack store: import → Stripe/PayPal checkout → fulfillment.',
      'Multi-channel sync: Amazon, eBay, Etsy listings.',
      'Dynamic pricing: competitor scraping & margin tools.',
      'Order tracking: client portal for shipments & returns.',
    ],
  },
  {
    Icon: FaBullhorn,
    title: 'Digital Marketing & SEO',
    points: [
      'SEO audits & schema: on-page & technical.',
      'Paid ads: AI-optimized Facebook/Google campaigns.',
      'Social automations: scheduled posts & engagement bots.',
      'Email drip campaigns: personalized AI-copy.',
    ],
  },
  {
    Icon: FaCloud,
    title: 'Cloud & DevOps',
    points: [
      'Cloud migration: AWS/GCP/Azure, Docker & K8s.',
      'Serverless: Lambdas & Cloud Functions.',
      'Monitoring & security: logging, alerts & backups.',
    ],
  },
  {
    Icon: FaRobot,
    title: 'IoT, Robotics & Education',
    points: [
      'Arduino/RPi kits: PCB assembly & firmware.',
      'STEM platforms: interactive simulators & challenges.',
      'Smart home: MQTT dashboards & voice control.',
    ],
  },
  {
    Icon: FaVrCardboard,
    title: 'Immersive & Emerging Tech',
    points: [
      'AR/VR prototypes: WebAR & Unity tours.',
      'Web3: NFT sites & smart contract audits.',
      'Metaverse showrooms: 3D galleries.',
    ],
  },
  {
    Icon: FaGamepad,
    title: 'Game Development',
    points: [
      '2D/3D games: Unity, Unreal & WebGL.',
      'Educational games: gamified STEM learning.',
      'Mobile casual titles: cross-platform & ad-ready.',
    ],
  },
];

const Numbered = styled(Box)(({ theme, selected }) => ({
  width: 32,
  height: 32,
  borderRadius: '50%',
  background: selected
    ? theme.palette.primary.main
    : theme.palette.grey[300],
  color: selected ? '#fff' : theme.palette.text.primary,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 600,
}));

export default function ServicesSection() {
  const theme  = useTheme();
  const isMdUp  = useMediaQuery(theme.breakpoints.up('md'));
  const [idx, setIdx] = useState(0);

  const prev = () => setIdx((i) => Math.max(i - 1, 0));
  const next = () => setIdx((i) => Math.min(i + 1, services.length - 1));

  const { Icon, title, points } = services[idx];

  return (
    <Box component="section" sx={{ py: { xs: 6, md: 10 }, bgcolor: 'background.default' }}>
      <Container maxWidth="lg">
        <Typography
          variant="h4"
          align="center"
          fontWeight={700}
          color="primary"
          gutterBottom
        >
          Our Services
        </Typography>

        <Typography
          variant="subtitle1"
          align="center"
          color="text.secondary"
          sx={{ mb: 4 }}
        >
          Click any service to see more details
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 4,
          }}
        >
          {/* Left list */}
          <Grid
            container
            spacing={2}
            sx={{ flex: 2 }}
          >
            {services.map((s, i) => (
              <Grid key={s.title} item xs={12} sm={6}>
                <Box
                  onClick={() => setIdx(i)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: 2,
                    borderRadius: 2,
                    cursor: 'pointer',
                    bgcolor: i === idx ? '#f7e28b' : 'white',
                    transition: 'background .2s',
                    '&:hover': { bgcolor: '#f7e28b' },
                  }}
                >
                  <Numbered selected={i === idx}>{i + 1}</Numbered>
                  <Typography
                    variant="body1"
                    sx={{ ml: 2, fontWeight: i === idx ? 600 : 400 }}
                  >
                    {s.title}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          {/* Right detail panel */}
          <Box
            sx={{
              flex: 1,
              bgcolor: 'primary.main',
              color: 'common.white',
              borderRadius: 2,
              p: 4,
              position: 'relative',
            }}
          >
            {/* Prev/Next */}
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: 8,
                transform: 'translateY(-50%)',
              }}
            >
              <IconButton
                onClick={prev}
                disabled={idx === 0}
                sx={{ color: 'common.white' }}
              >
                <KeyboardArrowLeft />
              </IconButton>
            </Box>
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                right: 8,
                transform: 'translateY(-50%)',
              }}
            >
              <IconButton
                onClick={next}
                disabled={idx === services.length - 1}
                sx={{ color: 'common.white' }}
              >
                <KeyboardArrowRight />
              </IconButton>
            </Box>

            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Icon size={28} />
              <Typography variant="h5" component="h3" fontWeight={700} color='white'>
                {title}
              </Typography>
            </Box>

            <List dense disablePadding>
              {points.map((p, i) => (
                <ListItem key={i} sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32, color: 'info.light' }}>
                    <CheckCircleIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={p}
                    primaryTypographyProps={{ variant: 'body2', color: 'common.white' }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
